import type { SecondaryBlockerCode } from '../types/encounterWork';

/**
 * The Bill secondary queue's words (GDN-4023, contract gdn-3991 v1 §7).
 *
 * Copy for the blocker codes a row carries, the age of the primary's 835 in
 * days with the two thresholds the surfaces colour by, and the one fallback
 * for a row whose secondary payer has no name. One module because Home's
 * card and the Claims tab's columns both render these facts, and a biller
 * must read the same words about the same claim on either surface.
 */

export const SECONDARY_BLOCKER_LABELS: Record<SecondaryBlockerCode, string> = {
  payer_unresolved: 'Secondary payer not set up',
  policy_order_disputed: 'Verify policy order',
  primary_open_fix: 'Fix the primary first',
  primary_claim_number_missing: 'Primary claim number missing',
};

/**
 * A code the contract has not named renders as itself. The server said the
 * secondary is blocked; hiding the reason because the copy lags would hide
 * the blocker.
 */
export const secondaryBlockerLabel = (code: string): string =>
  (SECONDARY_BLOCKER_LABELS as Record<string, string>)[code] ?? code;

/** The row's `secondary_payer_name` is null: the order-2 coverage names no payer. */
export const SECONDARY_PAYER_UNSET_LABEL = 'Secondary payer not set';

/** Amber from thirty days, red from sixty (contract §7). */
export const ERA_AGE_WARNING_DAYS = 30;
export const ERA_AGE_ERROR_DAYS = 60;

export type EraAgeTone = 'default' | 'warning' | 'error';

const MS_PER_DAY = 86_400_000;

/**
 * Whole days since the primary's driving 835, or null when the row carries
 * no date, an unreadable one, or one in the future. Null, never zero: an
 * invented zero would read as "the ERA came today".
 */
export function daysSinceEra(
  primaryEraAt: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!primaryEraAt) return null;
  const occurred = Date.parse(primaryEraAt);
  if (!Number.isFinite(occurred)) return null;
  const days = Math.floor((now - occurred) / MS_PER_DAY);
  return days >= 0 ? days : null;
}

export function eraAgeTone(days: number): EraAgeTone {
  if (days >= ERA_AGE_ERROR_DAYS) return 'error';
  if (days >= ERA_AGE_WARNING_DAYS) return 'warning';
  return 'default';
}

/** "1 day", "64 days". */
export const formatEraAge = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;
